<template>
    <AppLayout>
        <template #icon> <TeamReportsHeaderLogo /> </template>
        <template #title> {{ $t("team_reports.title") }} </template>
        <template #description> {{ $t("team_reports.description") }} </template>

        <template #actions>
            <TeamSwitcher :teams="teams" :selected-team="selectedTeam" />
        </template>

        <div class="p-5">
            <div class="flex justify-between">
                <div class="gap-2 flex items-center">
                    <p class="text-lg font-medium tracking-tight">
                        {{ selectedTeam.name }}
                    </p>

                    <div
                        class="rounded-md border-iron-300 bg-white px-2 text-xs font-medium tracking-tight text-iron-700 border py-[3px]"
                    >
                        {{ teamMembers.length }}
                        {{ $t("team_reports.members", teamMembers.length) }}
                    </div>
                </div>

                <div class="gap-4 flex">
                    <div>
                        <TeamMemberSwitcher
                            :team-members="teamMembers"
                            :selected-team-member="selectedUser"
                            @select-user="selectUser"
                        />
                    </div>
                    <div>
                        <EventTypeSwitcher
                            :event-types="eventTypes"
                            :team-id="selectedTeam.id"
                            :selected-event-type="selectedEventType"
                            @update:selected-event-type="selectEventType"
                        />
                    </div>
                </div>
            </div>

            <div>
                <div>
                    <div
                        class="mt-4 rounded-xl border-iron-200 bg-white px-10 py-5 shadow-sm h-full border"
                    >
                        <h2 class="text-lg font-semibold tracking-tight">
                            {{ $t("team_reports.chart_card.title") }}
                        </h2>

                        <Tabs class="mt-4" default-value="past">
                            <TabsList>
                                <TabsTrigger value="past">
                                    {{ $t("team_reports.chart_card.monthly") }}
                                </TabsTrigger>
                                <TabsTrigger value="past_by_user">
                                    {{ $t("team_reports.chart_card.by_user") }}
                                </TabsTrigger>
                                <TabsTrigger value="past_by_event">
                                    {{ $t("team_reports.chart_card.by_event") }}
                                </TabsTrigger>
                            </TabsList>
                            <TabsContent class="pt-10" value="past">
                                <TotalChart :data="pastReservationsByMonth" />
                            </TabsContent>
                            <TabsContent class="pt-10" value="past_by_user">
                                <DonutChart :data="pastReservationsByUser" />
                            </TabsContent>
                            <TabsContent class="pt-10" value="past_by_event">
                                <DonutChart
                                    :data="pastReservationsByEventType"
                                />
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            </div>

            <div
                class="mb-12 mt-12 rounded-xl border-iron-200 bg-white shadow-sm border"
            >
                <div class="p-5">
                    <h1
                        class="mt-2 text-lg font-medium tracking-tight text-iron-700"
                    >
                        {{ $t("team_reports.reservations") }}
                    </h1>
                </div>
                <ReservationsTable
                    :data="reservations"
                    :team-members="teamMembers"
                    @prev-page="prevPage"
                    @next-page="nextPage"
                    @set-page="setPage"
                />
            </div>
        </div>
    </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from "../../../../Layouts/AppLayout.vue";

import TotalChart from "./components/TotalChart.vue";
import DonutChart from "./components/DonutChart.vue";
import ReservationsTable from "./components/ReservationsTable.vue";
import TeamSwitcher from "./components/TeamSwitcher.vue";
import EventTypeSwitcher from "./components/EventTypeSwitcher.vue";
import TeamMemberSwitcher from "./components/TeamMemberSwitcher.vue";
import { User } from "../EventTypes/types/EventType";
import { EventType } from "../EventTypes/types/EventType";
import { Team } from "../Teams/types/Team";
import { PastReservation } from "./types";
import { ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { Reservation } from "../Dashboard/components/event.vue";
import type { PaginatedData } from "../../../../types/Pagination";
import { useUrlSearchParams } from "@vueuse/core";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChartArea } from "lucide-vue-next";
import TeamReportsHeaderLogo from "./components/TeamReportsHeaderLogo.svg";

type Params = {
    userId: string;
    eventTypeIds: string;
    page: number;
};

const params = useUrlSearchParams<Params>();

params;

const props = defineProps<{
    pastReservationsByMonth: PastReservation[];
    pastReservationsByUser: PastReservation[];
    pastReservationsByEventType: PastReservation[];
    upcomingReservations: PaginatedData<Reservation>;
    canceledReservations: PaginatedData<Reservation>;
    recentlyBookedReservations: PaginatedData<Reservation>;
    teamMembers: User[];
    eventTypes: EventType[];
    teams: Team[];
    selectedTeam: Team;
    reservations: PaginatedData<Reservation>;
}>();

console.log(props);

const selectedUser = ref<User | undefined>(
    props.teamMembers.find((member) => member.id.toString() === params.userId),
);
const selectedEventType = ref<string>(params.eventTypeIds);
const currentPage = ref(1);

console.log(props.reservations);

function visitWithQueryData() {
    router.visit(
        route("app.teams.reports.index", {
            team: props.selectedTeam.id,
            _query: {
                userId: selectedUser.value?.id,
                eventTypeIds: [selectedEventType.value],
                page: currentPage.value,
            },
        }),

        {
            preserveState: true,
            only: [
                "pastReservationsByMonth",
                "pastReservationsByUser",
                "pastReservationsByEventType",
                "reservations",
                "upcomingReservations",
                "canceledReservations",
                "recentlyBookedReservations",
            ],
        },
    );
}

function selectUser(user: User | undefined) {
    if (!user) {
        selectedUser.value = undefined;
    } else if (selectedUser.value && user.id === selectedUser.value.id) {
        selectedUser.value = undefined;
    } else {
        selectedUser.value = user;
    }
    currentPage.value = 1;

    visitWithQueryData();
}

function nextPage() {
    currentPage.value++;
    visitWithQueryData();
}

function prevPage() {
    currentPage.value--;
    visitWithQueryData();
}

function setPage(page: number) {
    currentPage.value = page;
    visitWithQueryData();
}

function selectEventType(eventTypeId: string) {
    selectedEventType.value = eventTypeId;
    currentPage.value = 1;
    visitWithQueryData();
}
</script>

<style>
.gradient {
    border: 1.8px solid;

    border-image-source: linear-gradient(
        137.17deg,
        #ff8d47 10.41%,
        #ff7447 22.14%,
        #ff4247 46.48%,
        #e92a5b 61%,
        #dd1c67 69.21%,
        #d31071 77.36%,
        #cf0283 89.83%
    );
}
</style>
